import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { 
  Users, 
  Calendar, 
  CheckCircle, 
  Clock, 
  Download, 
  LogOut,
  ChevronDown,
  ChevronUp,
  Mail,
  Phone,
  CreditCard,
  Euro,
  RotateCcw,
  Star,
  MessageSquare,
  HelpCircle,
  Plus,
  Edit2,
  Trash2,
  Save,
  X,
  ArrowUp,
  ArrowDown,
  Eye,
  EyeOff,
  Inbox,
  UserCheck,
  UserX,
  BarChart3,
  TrendingUp,
  Wallet,
  PieChart,
  Bell,
  Send,
  XCircle,
  RefreshCw,
  FileText,
  Settings,
  AlertTriangle,
  Trash2 as TrashIcon,
  Menu
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart as RechartsPie, Pie, Cell, Legend } from 'recharts';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminPage = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [loading, setLoading] = useState(false);
  const [updatingPayment, setUpdatingPayment] = useState(null);
  const [updatingAttendance, setUpdatingAttendance] = useState(null);
  
  // Tab management
  const [activeTab, setActiveTab] = useState('registrations');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [expandedMobileCard, setExpandedMobileCard] = useState(null);
  
  // Dashboard data
  const [dashboardData, setDashboardData] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [registrationsByDate, setRegistrationsByDate] = useState({}); // Registrierungen pro Termin
  const [loadingRegistrations, setLoadingRegistrations] = useState({});
  const [expandedDates, setExpandedDates] = useState([]); // Array für mehrere offene Termine

  // Reviews data
  const [reviews, setReviews] = useState([]);
  const [loadingReviews, setLoadingReviews] = useState(false);
  const [editingReview, setEditingReview] = useState(null);
  const [newReview, setNewReview] = useState({ text: '', author: '', rating: 5, is_active: true });
  const [showNewReviewForm, setShowNewReviewForm] = useState(false);

  // FAQ data
  const [faqs, setFaqs] = useState([]);
  const [loadingFaqs, setLoadingFaqs] = useState(false);
  const [editingFaq, setEditingFaq] = useState(null);
  const [newFaq, setNewFaq] = useState({ question: '', answer: '', is_active: true });
  const [showNewFaqForm, setShowNewFaqForm] = useState(false);

  // Contact messages
  const [contactMessages, setContactMessages] = useState([]);
  const [loadingMessages, setLoadingMessages] = useState(false);

  // Statistics data
  const [statistics, setStatistics] = useState(null);
  const [loadingStatistics, setLoadingStatistics] = useState(false);
  const [statisticsPeriod, setStatisticsPeriod] = useState('current_month');

  // Reminder state
  const [reminderStatus, setReminderStatus] = useState(null);
  const [loadingReminderStatus, setLoadingReminderStatus] = useState(false);
  const [sendingReminders, setSendingReminders] = useState(false);

  // Certificate requests state
  const [certificateRequests, setCertificateRequests] = useState([]);
  const [loadingCertificates, setLoadingCertificates] = useState(false);
  const [updatingCertificate, setUpdatingCertificate] = useState(null);

  // Database reset state
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [resetConfirmText, setResetConfirmText] = useState('');
  const [resettingDatabase, setResettingDatabase] = useState(false);

  // Booking changes state (cancellations & reschedules)
  const [bookingChanges, setBookingChanges] = useState([]);
  const [bookingChangesSummary, setBookingChangesSummary] = useState({ total: 0, cancellations: 0, reschedules: 0 });
  const [loadingBookingChanges, setLoadingBookingChanges] = useState(false);

  // Delete registration state
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [registrationToDelete, setRegistrationToDelete] = useState(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deletingRegistration, setDeletingRegistration] = useState(false);

  // Store credentials for API calls
  const [authHeader, setAuthHeader] = useState('');

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setLoginError('');

    try {
      const credentials = btoa(`${username}:${password}`);
      const response = await axios.post(
        `${API_URL}/api/admin/login`,
        {},
        {
          headers: {
            'Authorization': `Basic ${credentials}`
          }
        }
      );
      
      if (response.data.status === 'success') {
        setAuthHeader(`Basic ${credentials}`);
        setIsLoggedIn(true);
        sessionStorage.setItem('adminAuth', credentials);
      }
    } catch (error) {
      setLoginError('Ungültige Anmeldedaten');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    setIsLoggedIn(false);
    setAuthHeader('');
    setUsername('');
    setPassword('');
    sessionStorage.removeItem('adminAuth');
  };

  // Fetch Dashboard
  const fetchDashboard = useCallback(async () => {
    if (!authHeader) return;
    
    try {
      const response = await axios.get(`${API_URL}/api/admin/dashboard`, {
        headers: { 'Authorization': authHeader }
      });
      setDashboardData(response.data);
    } catch (error) {
      console.error('Error fetching dashboard:', error);
      if (error.response?.status === 401) {
        handleLogout();
      }
    }
  }, [authHeader]);

  // Fetch Reviews
  const fetchReviews = useCallback(async () => {
    if (!authHeader) return;
    setLoadingReviews(true);
    
    try {
      const response = await axios.get(`${API_URL}/api/admin/reviews`, {
        headers: { 'Authorization': authHeader }
      });
      setReviews(response.data);
    } catch (error) {
      console.error('Error fetching reviews:', error);
      toast.error('Fehler beim Laden der Bewertungen');
    } finally {
      setLoadingReviews(false);
    }
  }, [authHeader]);

  // Fetch FAQs
  const fetchFaqs = useCallback(async () => {
    if (!authHeader) return;
    setLoadingFaqs(true);
    
    try {
      const response = await axios.get(`${API_URL}/api/admin/faqs`, {
        headers: { 'Authorization': authHeader }
      });
      setFaqs(response.data);
    } catch (error) {
      console.error('Error fetching FAQs:', error);
      toast.error('Fehler beim Laden der FAQs');
    } finally {
      setLoadingFaqs(false);
    }
  }, [authHeader]);

  // Fetch Contact Messages
  const fetchContactMessages = useCallback(async () => {
    if (!authHeader) return;
    setLoadingMessages(true);
    
    try {
      const response = await axios.get(`${API_URL}/api/admin/contact-messages`, {
        headers: { 'Authorization': authHeader }
      });
      setContactMessages(response.data);
    } catch (error) {
      console.error('Error fetching contact messages:', error);
    } finally {
      setLoadingMessages(false);
    }
  }, [authHeader]);

  // Fetch Statistics
  const fetchStatistics = useCallback(async (period = statisticsPeriod) => {
    if (!authHeader) return;
    setLoadingStatistics(true);
    
    try {
      const response = await axios.get(`${API_URL}/api/admin/statistics?period=${period}`, {
        headers: { 'Authorization': authHeader }
      });
      setStatistics(response.data);
    } catch (error) {
      console.error('Error fetching statistics:', error);
      toast.error('Fehler beim Laden der Statistiken');
    } finally {
      setLoadingStatistics(false);
    }
  }, [authHeader, statisticsPeriod]);

  // Fetch Reminder Status
  const fetchReminderStatus = useCallback(async () => {
    if (!authHeader) return;
    setLoadingReminderStatus(true);
    
    try {
      const response = await axios.get(`${API_URL}/api/admin/reminder-status`, {
        headers: { 'Authorization': authHeader }
      });
      setReminderStatus(response.data);
    } catch (error) {
      console.error('Error fetching reminder status:', error);
    } finally {
      setLoadingReminderStatus(false);
    }
  }, [authHeader]);

  // Send Reminders
  const handleSendReminders = async () => {
    if (!authHeader) return;
    
    if (!window.confirm('Möchten Sie wirklich Erinnerungs-E-Mails an alle Teilnehmer für morgige Kurse senden?')) {
      return;
    }
    
    setSendingReminders(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/admin/send-reminders`,
        {},
        { headers: { 'Authorization': authHeader } }
      );
      
      const data = response.data;
      if (data.reminders_sent > 0) {
        toast.success(`${data.reminders_sent} Erinnerung(en) gesendet!`);
      } else if (data.already_sent > 0) {
        toast.info(`Alle ${data.already_sent} Erinnerungen wurden bereits gesendet.`);
      } else {
        toast.info(data.message || 'Keine Erinnerungen zu senden.');
      }
      
      // Refresh status
      fetchReminderStatus();
    } catch (error) {
      console.error('Error sending reminders:', error);
      toast.error('Fehler beim Senden der Erinnerungen');
    } finally {
      setSendingReminders(false);
    }
  };

  // Fetch Certificate Requests
  const fetchCertificateRequests = useCallback(async () => {
    if (!authHeader) return;
    setLoadingCertificates(true);
    
    try {
      const response = await axios.get(`${API_URL}/api/admin/certificate-requests`, {
        headers: { 'Authorization': authHeader }
      });
      setCertificateRequests(response.data);
    } catch (error) {
      console.error('Error fetching certificate requests:', error);
      toast.error('Fehler beim Laden der Ersatzbescheinigungen');
    } finally {
      setLoadingCertificates(false);
    }
  }, [authHeader]);

  // Fetch Booking Changes (Cancellations & Reschedules)
  const fetchBookingChanges = useCallback(async () => {
    if (!authHeader) return;
    setLoadingBookingChanges(true);
    
    try {
      const response = await axios.get(`${API_URL}/api/admin/booking-changes`, {
        headers: { 'Authorization': authHeader }
      });
      setBookingChanges(response.data.changes || []);
      setBookingChangesSummary(response.data.summary || { total: 0, cancellations: 0, reschedules: 0 });
    } catch (error) {
      console.error('Error fetching booking changes:', error);
      toast.error('Fehler beim Laden der Buchungsänderungen');
    } finally {
      setLoadingBookingChanges(false);
    }
  }, [authHeader]);

  // Delete Booking Change (Storno/Umbuchung entry)
  const handleDeleteBookingChange = async (changeId) => {
    if (!authHeader) return;
    
    if (!window.confirm('Möchten Sie diesen Eintrag wirklich löschen?')) {
      return;
    }
    
    try {
      await axios.delete(
        `${API_URL}/api/admin/booking-changes/${changeId}`,
        { headers: { 'Authorization': authHeader } }
      );
      
      // Remove from local state
      setBookingChanges(prev => prev.filter(c => c.id !== changeId));
      // Update summary
      setBookingChangesSummary(prev => ({
        ...prev,
        total: Math.max(0, prev.total - 1),
        cancellations: bookingChanges.find(c => c.id === changeId)?.change_type === 'cancellation' 
          ? Math.max(0, prev.cancellations - 1) 
          : prev.cancellations,
        reschedules: bookingChanges.find(c => c.id === changeId)?.change_type === 'reschedule' 
          ? Math.max(0, prev.reschedules - 1) 
          : prev.reschedules
      }));
      
      toast.success('Eintrag gelöscht');
    } catch (error) {
      console.error('Error deleting booking change:', error);
      toast.error('Fehler beim Löschen');
    }
  };

  // Update Certificate Request Status
  const handleUpdateCertificateStatus = async (requestId, newStatus) => {
    if (!authHeader) return;
    
    setUpdatingCertificate(requestId);
    try {
      await axios.patch(
        `${API_URL}/api/admin/certificate-requests/${requestId}/status`,
        { status: newStatus },
        { headers: { 'Authorization': authHeader } }
      );
      
      setCertificateRequests(prev => 
        prev.map(req => 
          req.id === requestId 
            ? { ...req, status: newStatus }
            : req
        )
      );
      
      if (newStatus === 'bereit') {
        toast.success('Status aktualisiert & Abholbenachrichtigung gesendet!');
      } else {
        toast.success('Status aktualisiert');
      }
    } catch (error) {
      console.error('Error updating certificate status:', error);
      toast.error('Fehler beim Aktualisieren');
    } finally {
      setUpdatingCertificate(null);
    }
  };

  // Delete Certificate Request
  const handleDeleteCertificateRequest = async (requestId) => {
    if (!authHeader) return;
    
    if (!window.confirm('Möchten Sie diese Anfrage wirklich löschen?')) {
      return;
    }
    
    try {
      await axios.delete(
        `${API_URL}/api/admin/certificate-requests/${requestId}`,
        { headers: { 'Authorization': authHeader } }
      );
      
      setCertificateRequests(prev => prev.filter(req => req.id !== requestId));
      toast.success('Anfrage gelöscht');
    } catch (error) {
      console.error('Error deleting certificate request:', error);
      toast.error('Fehler beim Löschen');
    }
  };

  const fetchRegistrations = async (dateId) => {
    if (!authHeader) return;
    
    setLoadingRegistrations(prev => ({ ...prev, [dateId]: true }));
    try {
      const response = await axios.get(`${API_URL}/api/admin/registrations/${dateId}`, {
        headers: { 'Authorization': authHeader }
      });
      setRegistrationsByDate(prev => ({ ...prev, [dateId]: response.data }));
      setSelectedDate(dateId);
    } catch (error) {
      console.error('Error fetching registrations:', error);
    } finally {
      setLoadingRegistrations(prev => ({ ...prev, [dateId]: false }));
    }
  };

  const handleUpdatePaymentStatus = async (registrationId, newStatus, dateId) => {
    if (!authHeader) return;
    
    setUpdatingPayment(registrationId);
    try {
      await axios.patch(
        `${API_URL}/api/admin/registrations/${registrationId}/payment-status`,
        { payment_status: newStatus },
        { headers: { 'Authorization': authHeader } }
      );
      
      setRegistrationsByDate(prev => ({
        ...prev,
        [dateId]: prev[dateId]?.map(reg => 
          reg.id === registrationId 
            ? { ...reg, payment_status: newStatus }
            : reg
        ) || []
      }));
      
      fetchDashboard();
      toast.success('Zahlungsstatus aktualisiert');
    } catch (error) {
      console.error('Error updating payment status:', error);
      toast.error('Fehler beim Aktualisieren');
    } finally {
      setUpdatingPayment(null);
    }
  };

  const handleUpdateAttendance = async (registrationId, isPresent, dateId) => {
    if (!authHeader) return;
    
    setUpdatingAttendance(registrationId);
    try {
      await axios.patch(
        `${API_URL}/api/admin/registrations/${registrationId}/attendance`,
        { is_present: isPresent },
        { headers: { 'Authorization': authHeader } }
      );
      
      setRegistrationsByDate(prev => ({
        ...prev,
        [dateId]: prev[dateId]?.map(reg => 
          reg.id === registrationId 
            ? { ...reg, is_present: isPresent }
            : reg
        ) || []
      }));
      
      toast.success(isPresent ? 'Als anwesend markiert' : 'Anwesenheit entfernt');
    } catch (error) {
      console.error('Error updating attendance:', error);
      toast.error('Fehler beim Aktualisieren');
    } finally {
      setUpdatingAttendance(null);
    }
  };

  const handleExportCSV = async (dateId = null) => {
    if (!authHeader) return;
    
    const url = dateId 
      ? `${API_URL}/api/admin/export/csv/${dateId}`
      : `${API_URL}/api/admin/export/csv`;
    
    try {
      const response = await axios.get(url, {
        headers: { 'Authorization': authHeader },
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { type: 'text/csv' });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = dateId ? `anmeldungen_${dateId}.csv` : `alle_anmeldungen.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error('Error exporting CSV:', error);
    }
  };

  // DATABASE RESET HANDLER
  const handleDatabaseReset = async () => {
    if (resetConfirmText !== 'RESET') {
      toast.error('Bitte geben Sie "RESET" ein, um zu bestätigen');
      return;
    }
    
    setResettingDatabase(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/admin/reset-database`,
        null,
        { 
          headers: { 
            'Authorization': authHeader,
            'Content-Type': 'application/json'
          } 
        }
      );
      
      if (response.data.success) {
        const results = response.data.results;
        toast.success(
          `Datenbank bereinigt! ${results.registrations_deleted} Anmeldungen, ` +
          `${results.booking_changes_deleted} Buchungsänderungen, ` +
          `${results.certificate_requests_deleted} Zertifikate gelöscht.`
        );
        setShowResetConfirm(false);
        setResetConfirmText('');
        
        // Refresh all data
        fetchDashboard();
        fetchStatistics();
      }
    } catch (error) {
      console.error('Error resetting database:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Unbekannter Fehler';
      toast.error(`Fehler: ${errorMsg}`);
    } finally {
      setResettingDatabase(false);
    }
  };

  const toggleDateExpansion = (dateId) => {
    if (expandedDates.includes(dateId)) {
      // Termin schließen
      setExpandedDates(expandedDates.filter(id => id !== dateId));
    } else {
      // Termin öffnen (andere bleiben offen)
      setExpandedDates([...expandedDates, dateId]);
      fetchRegistrations(dateId);
    }
  };

  // REVIEW CRUD HANDLERS
  const handleCreateReview = async () => {
    if (!newReview.text || !newReview.author) {
      toast.error('Bitte füllen Sie alle Pflichtfelder aus');
      return;
    }
    
    try {
      await axios.post(
        `${API_URL}/api/admin/reviews`,
        newReview,
        { headers: { 'Authorization': authHeader } }
      );
      toast.success('Bewertung erstellt');
      setNewReview({ text: '', author: '', rating: 5, is_active: true });
      setShowNewReviewForm(false);
      fetchReviews();
    } catch (error) {
      console.error('Error creating review:', error);
      toast.error('Fehler beim Erstellen');
    }
  };

  const handleUpdateReview = async (reviewId) => {
    if (!editingReview) return;
    
    try {
      await axios.put(
        `${API_URL}/api/admin/reviews/${reviewId}`,
        editingReview,
        { headers: { 'Authorization': authHeader } }
      );
      toast.success('Bewertung aktualisiert');
      setEditingReview(null);
      fetchReviews();
    } catch (error) {
      console.error('Error updating review:', error);
      toast.error('Fehler beim Aktualisieren');
    }
  };

  const handleDeleteReview = async (reviewId) => {
    if (!window.confirm('Bewertung wirklich löschen?')) return;
    
    try {
      await axios.delete(
        `${API_URL}/api/admin/reviews/${reviewId}`,
        { headers: { 'Authorization': authHeader } }
      );
      toast.success('Bewertung gelöscht');
      fetchReviews();
    } catch (error) {
      console.error('Error deleting review:', error);
      toast.error('Fehler beim Löschen');
    }
  };

  const handleToggleReviewActive = async (review) => {
    try {
      await axios.put(
        `${API_URL}/api/admin/reviews/${review.id}`,
        { is_active: !review.is_active },
        { headers: { 'Authorization': authHeader } }
      );
      fetchReviews();
      toast.success(review.is_active ? 'Bewertung deaktiviert' : 'Bewertung aktiviert');
    } catch (error) {
      console.error('Error toggling review:', error);
    }
  };

  const handleMoveReview = async (reviewId, direction) => {
    const index = reviews.findIndex(r => r.id === reviewId);
    if (index === -1) return;
    
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= reviews.length) return;
    
    const newOrder = reviews.map((r, i) => ({
      id: r.id,
      order: i === index ? newIndex + 1 : i === newIndex ? index + 1 : i + 1
    }));
    
    try {
      await axios.post(
        `${API_URL}/api/admin/reviews/reorder`,
        newOrder,
        { headers: { 'Authorization': authHeader } }
      );
      fetchReviews();
    } catch (error) {
      console.error('Error reordering reviews:', error);
    }
  };

  // FAQ CRUD HANDLERS
  const handleCreateFaq = async () => {
    if (!newFaq.question || !newFaq.answer) {
      toast.error('Bitte füllen Sie alle Pflichtfelder aus');
      return;
    }
    
    try {
      await axios.post(
        `${API_URL}/api/admin/faqs`,
        newFaq,
        { headers: { 'Authorization': authHeader } }
      );
      toast.success('FAQ erstellt');
      setNewFaq({ question: '', answer: '', is_active: true });
      setShowNewFaqForm(false);
      fetchFaqs();
    } catch (error) {
      console.error('Error creating FAQ:', error);
      toast.error('Fehler beim Erstellen');
    }
  };

  const handleUpdateFaq = async (faqId) => {
    if (!editingFaq) return;
    
    try {
      await axios.put(
        `${API_URL}/api/admin/faqs/${faqId}`,
        editingFaq,
        { headers: { 'Authorization': authHeader } }
      );
      toast.success('FAQ aktualisiert');
      setEditingFaq(null);
      fetchFaqs();
    } catch (error) {
      console.error('Error updating FAQ:', error);
      toast.error('Fehler beim Aktualisieren');
    }
  };

  const handleDeleteFaq = async (faqId) => {
    if (!window.confirm('FAQ wirklich löschen?')) return;
    
    try {
      await axios.delete(
        `${API_URL}/api/admin/faqs/${faqId}`,
        { headers: { 'Authorization': authHeader } }
      );
      toast.success('FAQ gelöscht');
      fetchFaqs();
    } catch (error) {
      console.error('Error deleting FAQ:', error);
      toast.error('Fehler beim Löschen');
    }
  };

  const handleToggleFaqActive = async (faq) => {
    try {
      await axios.put(
        `${API_URL}/api/admin/faqs/${faq.id}`,
        { is_active: !faq.is_active },
        { headers: { 'Authorization': authHeader } }
      );
      fetchFaqs();
      toast.success(faq.is_active ? 'FAQ deaktiviert' : 'FAQ aktiviert');
    } catch (error) {
      console.error('Error toggling FAQ:', error);
    }
  };

  const handleMoveFaq = async (faqId, direction) => {
    const index = faqs.findIndex(f => f.id === faqId);
    if (index === -1) return;
    
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= faqs.length) return;
    
    const newOrder = faqs.map((f, i) => ({
      id: f.id,
      order: i === index ? newIndex + 1 : i === newIndex ? index + 1 : i + 1
    }));
    
    try {
      await axios.post(
        `${API_URL}/api/admin/faqs/reorder`,
        newOrder,
        { headers: { 'Authorization': authHeader } }
      );
      fetchFaqs();
    } catch (error) {
      console.error('Error reordering FAQs:', error);
    }
  };

  // Contact Message Handlers
  const handleMarkMessageRead = async (messageId) => {
    try {
      await axios.patch(
        `${API_URL}/api/admin/contact-messages/${messageId}/read`,
        {},
        { headers: { 'Authorization': authHeader } }
      );
      fetchContactMessages();
    } catch (error) {
      console.error('Error marking message read:', error);
    }
  };

  const handleDeleteMessage = async (messageId) => {
    if (!window.confirm('Nachricht wirklich löschen?')) return;
    
    try {
      await axios.delete(
        `${API_URL}/api/admin/contact-messages/${messageId}`,
        { headers: { 'Authorization': authHeader } }
      );
      toast.success('Nachricht gelöscht');
      fetchContactMessages();
    } catch (error) {
      console.error('Error deleting message:', error);
    }
  };

  // Registration Delete Handlers
  const openDeleteModal = (registration) => {
    setRegistrationToDelete(registration);
    setDeleteConfirmText('');
    setDeleteModalOpen(true);
  };

  const closeDeleteModal = () => {
    setDeleteModalOpen(false);
    setRegistrationToDelete(null);
    setDeleteConfirmText('');
  };

  const handleDeleteRegistration = async () => {
    if (!registrationToDelete || deleteConfirmText !== 'LÖSCHEN') return;
    
    setDeletingRegistration(true);
    try {
      await axios.delete(
        `${API_URL}/api/admin/registrations/${registrationToDelete.id}`,
        { headers: { 'Authorization': authHeader } }
      );
      toast.success(`Anmeldung von ${registrationToDelete.first_name} ${registrationToDelete.last_name} gelöscht`);
      closeDeleteModal();
      
      // Refresh data
      fetchDashboard();
      // Alle offenen Termine neu laden
      expandedDates.forEach(dateId => {
        fetchRegistrations(dateId);
      });
    } catch (error) {
      console.error('Error deleting registration:', error);
      toast.error('Fehler beim Löschen der Anmeldung');
    } finally {
      setDeletingRegistration(false);
    }
  };

  // Check for existing session
  useEffect(() => {
    const storedAuth = sessionStorage.getItem('adminAuth');
    if (storedAuth) {
      setAuthHeader(`Basic ${storedAuth}`);
      setIsLoggedIn(true);
    }
  }, []);

  // Fetch data when tab changes
  useEffect(() => {
    if (isLoggedIn && authHeader) {
      if (activeTab === 'registrations') {
        fetchDashboard();
        fetchReminderStatus(); // Also load reminder status on registrations tab
      } else if (activeTab === 'reviews') {
        fetchReviews();
      } else if (activeTab === 'faqs') {
        fetchFaqs();
      } else if (activeTab === 'messages') {
        fetchContactMessages();
      } else if (activeTab === 'statistics') {
        fetchStatistics(statisticsPeriod);
      } else if (activeTab === 'certificates') {
        fetchCertificateRequests();
      } else if (activeTab === 'bookingChanges') {
        fetchBookingChanges();
      }
    }
  }, [isLoggedIn, authHeader, activeTab, fetchDashboard, fetchReviews, fetchFaqs, fetchContactMessages, fetchStatistics, fetchReminderStatus, fetchCertificateRequests, fetchBookingChanges, statisticsPeriod]);

  // Reload statistics when period changes
  const handlePeriodChange = (newPeriod) => {
    setStatisticsPeriod(newPeriod);
    fetchStatistics(newPeriod);
  };

  const getPaymentMethodLabel = (method) => {
    const labels = {
      'paypal': 'PayPal',
      'transfer': 'Überweisung',
      'cash': 'Barzahlung'
    };
    return labels[method] || method;
  };

  const getPaymentStatusBadge = (status) => {
    if (status === 'paid') {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
          <CheckCircle className="w-3 h-3" />
          Bezahlt
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700">
        <Clock className="w-3 h-3" />
        Ausstehend
      </span>
    );
  };

  const getBookingStatusBadge = (reg) => {
    const status = reg.status;
    if (status === 'cancelled') {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700">
          <XCircle className="w-3 h-3" />
          Storniert
        </span>
      );
    }
    if (status === 'cancel_no_refund') {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-slate-200 text-slate-700" title="Keine Erstattung">
          <XCircle className="w-3 h-3" />
          Abgesagt (o. Erst.)
        </span>
      );
    }
    if (status === 'reschedule_requested') {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-700" title={`Neuer Wunschtermin: ${reg.requested_new_date || 'N/A'}`}>
          <RefreshCw className="w-3 h-3" />
          Umbuchung angefragt
        </span>
      );
    }
    return null; // No badge for confirmed status (default)
  };

  // Login Form
  if (!isLoggedIn) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="w-16 h-16 bg-[#1e3a5f] rounded-full flex items-center justify-center mx-auto mb-4">
              <Users className="w-8 h-8 text-white" />
            </div>
            <CardTitle className="text-2xl text-[#1e3a5f]">Admin-Bereich</CardTitle>
            <p className="text-slate-500 text-sm mt-2">
              Erste Hilfe Bottrop – Verwaltung
            </p>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="username">Benutzername</Label>
                <Input
                  id="username"
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="Benutzername eingeben"
                  required
                  data-testid="admin-username"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Passwort</Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Passwort eingeben"
                  required
                  data-testid="admin-password"
                />
              </div>
              {loginError && (
                <p className="text-red-600 text-sm text-center" data-testid="login-error">
                  {loginError}
                </p>
              )}
              <Button 
                type="submit" 
                className="w-full bg-[#1e3a5f] hover:bg-[#2d4a6f]"
                disabled={loading}
                data-testid="admin-login-btn"
              >
                {loading ? 'Anmelden...' : 'Anmelden'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Dashboard
  return (
    <div className="min-h-screen bg-slate-100">
      {/* Header */}
      <header className="bg-[#1e3a5f] text-white py-3 sm:py-4 px-4 sm:px-6 shadow-md">
        <div className="max-w-7xl mx-auto flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 sm:gap-3">
            <Users className="w-5 h-5 sm:w-6 sm:h-6" />
            <h1 className="text-base sm:text-xl font-bold">Admin</h1>
          </div>
          <div className="flex items-center gap-2 sm:gap-4">
            <Button
              onClick={() => handleExportCSV()}
              variant="outline"
              size="sm"
              className="text-white border-white hover:bg-white/10 px-2 sm:px-3 h-8 sm:h-9 text-xs sm:text-sm"
              data-testid="export-all-csv"
            >
              <Download className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Alle exportieren</span>
            </Button>
            <Button
              onClick={handleLogout}
              variant="ghost"
              size="sm"
              className="text-white hover:bg-white/10 px-2 sm:px-3 h-8 sm:h-9 text-xs sm:text-sm"
              data-testid="admin-logout-btn"
            >
              <LogOut className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Abmelden</span>
            </Button>
          </div>
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div 
          className="md:hidden fixed inset-0 bg-black/50 z-40"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Mobile Sidebar Menu */}
      <div className={`md:hidden fixed top-0 left-0 h-full w-72 bg-white z-50 shadow-xl transform transition-transform duration-300 ${
        mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'
      }`}>
        <div className="bg-[#1e3a5f] text-white p-4 flex items-center justify-between">
          <span className="font-semibold">Navigation</span>
          <button onClick={() => setMobileMenuOpen(false)} className="p-1">
            <X className="w-5 h-5" />
          </button>
        </div>
        <nav className="p-2">
          {[
            { id: 'registrations', icon: Calendar, label: 'Anmeldungen' },
            { id: 'reviews', icon: Star, label: 'Bewertungen' },
            { id: 'faqs', icon: HelpCircle, label: 'FAQ' },
            { id: 'messages', icon: Inbox, label: 'Nachrichten', badge: contactMessages.filter(m => !m.is_read).length },
            { id: 'certificates', icon: FileText, label: 'Ersatzbescheinigungen', badge: certificateRequests.filter(r => r.status === 'neu').length, badgeColor: 'bg-orange-500' },
            { id: 'bookingChanges', icon: RefreshCw, label: 'Storno/Umbuchung' },
            { id: 'statistics', icon: BarChart3, label: 'Statistik' },
            { id: 'settings', icon: Settings, label: 'Einstellungen' },
          ].map((item) => (
            <button
              key={item.id}
              onClick={() => {
                setActiveTab(item.id);
                setMobileMenuOpen(false);
              }}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-colors relative ${
                activeTab === item.id
                  ? 'bg-red-50 text-red-600'
                  : 'text-slate-700 hover:bg-slate-100'
              }`}
            >
              <item.icon className="w-5 h-5" />
              <span className="font-medium">{item.label}</span>
              {item.badge > 0 && (
                <span className={`absolute right-3 ${item.badgeColor || 'bg-red-600'} text-white text-xs rounded-full w-5 h-5 flex items-center justify-center`}>
                  {item.badge}
                </span>
              )}
            </button>
          ))}
        </nav>
      </div>

      {/* Mobile Current Tab Display + Hamburger */}
      <div className="md:hidden bg-white border-b px-4 py-3 flex items-center justify-between">
        <button
          onClick={() => setMobileMenuOpen(true)}
          className="flex items-center gap-2 text-slate-700"
        >
          <Menu className="w-6 h-6" />
          <span className="font-medium">
            {activeTab === 'registrations' && 'Anmeldungen'}
            {activeTab === 'reviews' && 'Bewertungen'}
            {activeTab === 'faqs' && 'FAQ'}
            {activeTab === 'messages' && 'Nachrichten'}
            {activeTab === 'certificates' && 'Ersatzbescheinigungen'}
            {activeTab === 'bookingChanges' && 'Storno/Umbuchung'}
            {activeTab === 'statistics' && 'Statistik'}
            {activeTab === 'settings' && 'Einstellungen'}
          </span>
        </button>
        {(contactMessages.filter(m => !m.is_read).length > 0 || certificateRequests.filter(r => r.status === 'neu').length > 0) && (
          <span className="bg-red-600 text-white text-xs rounded-full px-2 py-0.5">
            {contactMessages.filter(m => !m.is_read).length + certificateRequests.filter(r => r.status === 'neu').length} neu
          </span>
        )}
      </div>

      {/* Desktop Tabs (hidden on mobile) */}
      <div className="hidden md:block bg-white border-b">
        <div className="max-w-7xl mx-auto px-6">
          <nav className="flex gap-1">
            <button
              onClick={() => setActiveTab('registrations')}
              className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'registrations'
                  ? 'border-red-600 text-red-600'
                  : 'border-transparent text-slate-600 hover:text-slate-900'
              }`}
            >
              <Calendar className="w-4 h-4 inline mr-2" />
              Anmeldungen
            </button>
            <button
              onClick={() => setActiveTab('reviews')}
              className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'reviews'
                  ? 'border-red-600 text-red-600'
                  : 'border-transparent text-slate-600 hover:text-slate-900'
              }`}
            >
              <Star className="w-4 h-4 inline mr-2" />
              Bewertungen
            </button>
            <button
              onClick={() => setActiveTab('faqs')}
              className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'faqs'
                  ? 'border-red-600 text-red-600'
                  : 'border-transparent text-slate-600 hover:text-slate-900'
              }`}
            >
              <HelpCircle className="w-4 h-4 inline mr-2" />
              FAQ
            </button>
            <button
              onClick={() => setActiveTab('messages')}
              className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors relative ${
                activeTab === 'messages'
                  ? 'border-red-600 text-red-600'
                  : 'border-transparent text-slate-600 hover:text-slate-900'
              }`}
            >
              <Inbox className="w-4 h-4 inline mr-2" />
              Nachrichten
              {contactMessages.filter(m => !m.is_read).length > 0 && (
                <span className="absolute -top-1 -right-1 bg-red-600 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                  {contactMessages.filter(m => !m.is_read).length}
                </span>
              )}
            </button>
            <button
              onClick={() => setActiveTab('certificates')}
              className={`relative px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'certificates'
                  ? 'border-red-600 text-red-600'
                  : 'border-transparent text-slate-600 hover:text-slate-900'
              }`}
            >
              <FileText className="w-4 h-4 inline mr-2" />
              Ersatzbescheinigungen
              {certificateRequests.filter(r => r.status === 'neu').length > 0 && (
                <span className="absolute -top-1 -right-1 bg-orange-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                  {certificateRequests.filter(r => r.status === 'neu').length}
                </span>
              )}
            </button>
            <button
              onClick={() => setActiveTab('bookingChanges')}
              className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'bookingChanges'
                  ? 'border-red-600 text-red-600'
                  : 'border-transparent text-slate-600 hover:text-slate-900'
              }`}
            >
              <RefreshCw className="w-4 h-4 inline mr-2" />
              Storno/Umbuchung
            </button>
            <button
              onClick={() => setActiveTab('statistics')}
              className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'statistics'
                  ? 'border-red-600 text-red-600'
                  : 'border-transparent text-slate-600 hover:text-slate-900'
              }`}
            >
              <BarChart3 className="w-4 h-4 inline mr-2" />
              Statistik
            </button>
            <button
              onClick={() => setActiveTab('settings')}
              className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'settings'
                  ? 'border-red-600 text-red-600'
                  : 'border-transparent text-slate-600 hover:text-slate-900'
              }`}
            >
              <Settings className="w-4 h-4 inline mr-2" />
              Einstellungen
            </button>
          </nav>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto p-4 sm:p-6">
        
        {/* REGISTRATIONS TAB */}
        {activeTab === 'registrations' && (
          <>
            {/* Stats Overview */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 mb-8">
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                      <Calendar className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Termine</p>
                      <p className="text-2xl font-bold text-[#1e3a5f]">{dashboardData.length}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                      <Users className="w-5 h-5 text-green-600" />
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Anmeldungen</p>
                      <p className="text-2xl font-bold text-[#1e3a5f]">
                        {dashboardData.reduce((sum, d) => sum + d.total_registrations, 0)}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                      <CheckCircle className="w-5 h-5 text-emerald-600" />
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Bezahlt</p>
                      <p className="text-2xl font-bold text-[#1e3a5f]">
                        {dashboardData.reduce((sum, d) => sum + d.paid_registrations, 0)}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-yellow-100 rounded-lg flex items-center justify-center">
                      <Clock className="w-5 h-5 text-yellow-600" />
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Ausstehend</p>
                      <p className="text-2xl font-bold text-[#1e3a5f]">
                        {dashboardData.reduce((sum, d) => sum + d.pending_registrations, 0)}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Reminder Section */}
            {reminderStatus && reminderStatus.courses.length > 0 && (
              <Card className="mb-8 border-orange-200 bg-orange-50">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                        <Bell className="w-5 h-5 text-orange-600" />
                      </div>
                      <div>
                        <CardTitle className="text-orange-800 text-lg">Erinnerungs-E-Mails</CardTitle>
                        <p className="text-sm text-orange-600">Kurse morgen ({reminderStatus.tomorrow_date})</p>
                      </div>
                    </div>
                    <Button
                      onClick={handleSendReminders}
                      disabled={sendingReminders || reminderStatus.total_pending === 0}
                      className="bg-orange-600 hover:bg-orange-700"
                    >
                      {sendingReminders ? (
                        'Sende...'
                      ) : (
                        <>
                          <Send className="w-4 h-4 mr-2" />
                          {reminderStatus.total_pending > 0 
                            ? `${reminderStatus.total_pending} Erinnerung(en) senden`
                            : 'Alle gesendet'
                          }
                        </>
                      )}
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {reminderStatus.courses.map((course) => (
                      <div key={course.date_id} className="flex items-center justify-between p-3 bg-white rounded-lg">
                        <div>
                          <p className="font-medium text-slate-700">{course.display_date}</p>
                          <p className="text-sm text-slate-500">{course.total_registrations} Teilnehmer</p>
                        </div>
                        <div className="flex items-center gap-4">
                          <div className="text-right">
                            <p className="text-sm text-green-600">
                              <CheckCircle className="w-3 h-3 inline mr-1" />
                              {course.reminders_sent} gesendet
                            </p>
                            {course.reminders_pending > 0 && (
                              <p className="text-sm text-orange-600">
                                <Clock className="w-3 h-3 inline mr-1" />
                                {course.reminders_pending} ausstehend
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Dates List */}
            <Card>
              <CardHeader>
                <CardTitle className="text-[#1e3a5f]">Termine & Anmeldungen</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {dashboardData.filter(d => d.total_registrations > 0 || d.spots_left < d.max_spots).map((date) => (
                    <div key={date.id} className="border rounded-lg overflow-hidden">
                      {/* Mobile Date Header */}
                      <div 
                        className="md:hidden p-4 bg-slate-50 cursor-pointer hover:bg-slate-100 transition-colors"
                        onClick={() => toggleDateExpansion(date.id)}
                        data-testid={`date-row-mobile-${date.id}`}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <p className="font-semibold text-[#1e3a5f] text-lg">{date.display_date}</p>
                          <div className="flex items-center gap-2">
                            <Button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleExportCSV(date.id);
                              }}
                              variant="outline"
                              size="sm"
                              className="h-8 w-8 p-0"
                              disabled={date.total_registrations === 0}
                            >
                              <Download className="w-4 h-4" />
                            </Button>
                            {expandedDates.includes(date.id) ? (
                              <ChevronUp className="w-5 h-5 text-slate-400" />
                            ) : (
                              <ChevronDown className="w-5 h-5 text-slate-400" />
                            )}
                          </div>
                        </div>
                        <p className="text-sm text-slate-500 mb-3">{date.time} · {date.location}</p>
                        <div className="flex gap-4">
                          <div className="flex-1 bg-white rounded-lg p-2 text-center">
                            <p className="text-xs text-slate-500">Anmeldungen</p>
                            <p className="font-bold text-[#1e3a5f] text-lg">{date.total_registrations}</p>
                          </div>
                          <div className="flex-1 bg-white rounded-lg p-2 text-center">
                            <p className="text-xs text-slate-500">Freie Plätze</p>
                            <p className={`font-bold text-lg ${date.spots_left > 5 ? 'text-green-600' : date.spots_left > 0 ? 'text-yellow-600' : 'text-red-600'}`}>
                              {date.spots_left}
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Desktop Date Header */}
                      <div 
                        className="hidden md:flex items-center justify-between p-4 bg-slate-50 cursor-pointer hover:bg-slate-100 transition-colors"
                        onClick={() => toggleDateExpansion(date.id)}
                        data-testid={`date-row-${date.id}`}
                      >
                        <div className="flex items-center gap-4">
                          <div className="text-center min-w-[60px]">
                            <p className="text-sm text-slate-500">ID</p>
                            <p className="font-bold text-[#1e3a5f]">{date.id}</p>
                          </div>
                          <div>
                            <p className="font-semibold text-[#1e3a5f]">{date.display_date}</p>
                            <p className="text-sm text-slate-500">{date.time} · {date.location}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-6">
                          <div className="text-center">
                            <p className="text-sm text-slate-500">Anmeldungen</p>
                            <p className="font-bold text-[#1e3a5f]">{date.total_registrations}</p>
                          </div>
                          <div className="text-center">
                            <p className="text-sm text-slate-500">Freie Plätze</p>
                            <p className={`font-bold ${date.spots_left > 5 ? 'text-green-600' : date.spots_left > 0 ? 'text-yellow-600' : 'text-red-600'}`}>
                              {date.spots_left}
                            </p>
                          </div>
                          <div className="flex items-center gap-2">
                            <Button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleExportCSV(date.id);
                              }}
                              variant="outline"
                              size="sm"
                              className="text-[#1e3a5f]"
                              disabled={date.total_registrations === 0}
                            >
                              <Download className="w-4 h-4" />
                            </Button>
                            {expandedDates.includes(date.id) ? (
                              <ChevronUp className="w-5 h-5 text-slate-400" />
                            ) : (
                              <ChevronDown className="w-5 h-5 text-slate-400" />
                            )}
                          </div>
                        </div>
                      </div>

                      {expandedDates.includes(date.id) && (
                        <div className="border-t bg-white">
                          {loadingRegistrations[date.id] ? (
                            <div className="p-8 text-center text-slate-500">Laden...</div>
                          ) : !registrationsByDate[date.id] || registrationsByDate[date.id].length === 0 ? (
                            <div className="p-8 text-center text-slate-500">
                              Keine Anmeldungen für diesen Termin
                            </div>
                          ) : (
                            <>
                              {/* Mobile Card View with Accordion */}
                              <div className="md:hidden divide-y">
                                {registrationsByDate[date.id].map((reg) => (
                                  <div key={reg.id} className="bg-white">
                                    {/* Tappable Header - Always visible */}
                                    <div 
                                      className="p-4 cursor-pointer active:bg-slate-50"
                                      onClick={() => setExpandedMobileCard(expandedMobileCard === reg.id ? null : reg.id)}
                                    >
                                      <div className="flex items-center justify-between">
                                        <div className="flex-1 min-w-0">
                                          <p className="font-semibold text-[#1e3a5f] text-base">
                                            {reg.first_name} {reg.last_name}
                                          </p>
                                          <div className="flex items-center gap-2 mt-1">
                                            {getPaymentStatusBadge(reg.payment_status)}
                                            {getBookingStatusBadge(reg)}
                                            {reg.is_present && (
                                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700">
                                                <UserCheck className="w-3 h-3" />
                                                Anwesend
                                              </span>
                                            )}
                                          </div>
                                        </div>
                                        <ChevronDown className={`w-5 h-5 text-slate-400 transition-transform ${expandedMobileCard === reg.id ? 'rotate-180' : ''}`} />
                                      </div>
                                    </div>
                                    
                                    {/* Expandable Details */}
                                    {expandedMobileCard === reg.id && (
                                      <div className="px-4 pb-4 space-y-4 bg-slate-50">
                                        {/* Contact Info */}
                                        <div className="space-y-2">
                                          <p className="text-xs font-semibold text-slate-500 uppercase">Kontakt</p>
                                          <a 
                                            href={`mailto:${reg.email}`} 
                                            className="flex items-center gap-3 p-3 bg-white rounded-lg text-blue-600"
                                          >
                                            <Mail className="w-5 h-5" />
                                            <span className="text-sm truncate">{reg.email}</span>
                                          </a>
                                          {reg.phone && (
                                            <a 
                                              href={`tel:${reg.phone}`} 
                                              className="flex items-center gap-3 p-3 bg-white rounded-lg text-blue-600"
                                            >
                                              <Phone className="w-5 h-5" />
                                              <span className="text-sm">{reg.phone}</span>
                                            </a>
                                          )}
                                        </div>
                                        
                                        {/* Payment Info */}
                                        <div className="space-y-2">
                                          <p className="text-xs font-semibold text-slate-500 uppercase">Zahlung</p>
                                          <div className="bg-white rounded-lg p-3">
                                            <div className="flex justify-between items-center">
                                              <span className="text-sm text-slate-600">{getPaymentMethodLabel(reg.payment_method)}</span>
                                              <span className="font-semibold text-[#1e3a5f]">{reg.amount?.toFixed(2)} €</span>
                                            </div>
                                            <p className="text-xs text-slate-500 mt-1">Ref: {reg.reference}</p>
                                          </div>
                                        </div>
                                        
                                        {/* Actions */}
                                        <div className="space-y-2">
                                          <p className="text-xs font-semibold text-slate-500 uppercase">Aktionen</p>
                                          <div className="grid grid-cols-2 gap-2">
                                            <Button
                                              size="sm"
                                              variant={reg.is_present ? "default" : "outline"}
                                              className={`h-11 text-sm ${
                                                reg.is_present 
                                                  ? 'bg-emerald-600 hover:bg-emerald-700 text-white' 
                                                  : 'text-slate-700 hover:text-emerald-600 hover:border-emerald-300'
                                              }`}
                                              onClick={() => handleUpdateAttendance(reg.id, !reg.is_present, date.id)}
                                              disabled={updatingAttendance === reg.id}
                                            >
                                              {reg.is_present ? (
                                                <><UserCheck className="w-4 h-4 mr-2" />Anwesend</>
                                              ) : (
                                                <><UserX className="w-4 h-4 mr-2" />Markieren</>
                                              )}
                                            </Button>
                                            
                                            {reg.payment_status === 'pending' && reg.status !== 'cancelled' ? (
                                              <Button
                                                size="sm"
                                                className="h-11 text-sm bg-green-600 hover:bg-green-700"
                                                onClick={() => handleUpdatePaymentStatus(reg.id, 'paid', date.id)}
                                                disabled={updatingPayment === reg.id}
                                              >
                                                <CheckCircle className="w-4 h-4 mr-2" />
                                                Bezahlt
                                              </Button>
                                            ) : reg.payment_status === 'paid' && reg.status !== 'cancelled' ? (
                                              <Button
                                                size="sm"
                                                variant="outline"
                                                className="h-11 text-sm text-slate-500"
                                                onClick={() => handleUpdatePaymentStatus(reg.id, 'pending', date.id)}
                                                disabled={updatingPayment === reg.id}
                                              >
                                                <RotateCcw className="w-4 h-4 mr-2" />
                                                Zurücksetzen
                                              </Button>
                                            ) : null}
                                          </div>
                                          
                                          {/* Delete Button */}
                                          <Button
                                            size="sm"
                                            variant="outline"
                                            className="w-full h-11 text-sm text-red-600 border-red-200 hover:bg-red-50 hover:border-red-300 mt-2"
                                            onClick={() => openDeleteModal(reg)}
                                          >
                                            <Trash2 className="w-4 h-4 mr-2" />
                                            Anmeldung löschen
                                          </Button>
                                        </div>
                                        
                                        {/* Additional Info */}
                                        <div className="text-xs text-slate-500 pt-2 border-t">
                                          <p>Paket: {reg.package_name || 'Komplettpaket'}</p>
                                          <p>Angemeldet: {reg.created_at_formatted || reg.created_at}</p>
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                ))}
                              </div>
                              
                              {/* Desktop Table View */}
                              <div className="hidden md:block overflow-x-auto">
                                <table className="w-full text-sm">
                                  <thead className="bg-slate-100">
                                    <tr>
                                      <th className="text-left p-3 font-semibold text-[#1e3a5f]">Name</th>
                                      <th className="text-left p-3 font-semibold text-[#1e3a5f]">Kontakt</th>
                                      <th className="text-left p-3 font-semibold text-[#1e3a5f]">Paket</th>
                                      <th className="text-left p-3 font-semibold text-[#1e3a5f]">Zahlung</th>
                                      <th className="text-left p-3 font-semibold text-[#1e3a5f]">Status</th>
                                      <th className="text-center p-3 font-semibold text-[#1e3a5f]">Anwesend</th>
                                      <th className="text-left p-3 font-semibold text-[#1e3a5f]">Angemeldet</th>
                                      <th className="text-center p-3 font-semibold text-[#1e3a5f]">Aktionen</th>
                                    </tr>
                                  </thead>
                                  <tbody className="divide-y">
                                    {registrationsByDate[date.id]?.map((reg) => (
                                      <tr key={reg.id} className="hover:bg-slate-50">
                                        <td className="p-3">
                                          <p className="font-medium text-[#1e3a5f]">
                                            {reg.first_name} {reg.last_name}
                                          </p>
                                          <p className="text-xs text-slate-500">Ref: {reg.reference}</p>
                                      </td>
                                      <td className="p-3">
                                        <div className="flex items-center gap-1 text-slate-600 mb-1">
                                          <Mail className="w-3 h-3" />
                                          <span className="text-xs">{reg.email}</span>
                                        </div>
                                        {reg.phone && (
                                          <div className="flex items-center gap-1 text-slate-600">
                                            <Phone className="w-3 h-3" />
                                            <span className="text-xs">{reg.phone}</span>
                                          </div>
                                        )}
                                      </td>
                                      <td className="p-3">
                                        <span className="text-xs text-slate-600">{reg.package_name || 'Komplettpaket'}</span>
                                      </td>
                                      <td className="p-3">
                                        <div className="flex items-center gap-1">
                                          <CreditCard className="w-4 h-4 text-slate-400" />
                                          <span>{getPaymentMethodLabel(reg.payment_method)}</span>
                                        </div>
                                        <div className="flex items-center gap-1 text-xs text-slate-500 mt-1">
                                          <Euro className="w-3 h-3" />
                                          <span>{reg.amount?.toFixed(2)} EUR</span>
                                        </div>
                                      </td>
                                      <td className="p-3">
                                        <div className="flex flex-col gap-1">
                                          <div className="flex items-center gap-2">
                                            {getPaymentStatusBadge(reg.payment_status)}
                                            {reg.payment_status === 'pending' && reg.status !== 'cancelled' ? (
                                              <Button
                                                size="sm"
                                                variant="outline"
                                                className="h-7 px-2 text-xs text-green-600 border-green-300 hover:bg-green-50"
                                                onClick={() => handleUpdatePaymentStatus(reg.id, 'paid', date.id)}
                                                disabled={updatingPayment === reg.id}
                                              >
                                                {updatingPayment === reg.id ? '...' : (
                                                  <>
                                                    <CheckCircle className="w-3 h-3 mr-1" />
                                                    Bezahlt
                                                  </>
                                                )}
                                              </Button>
                                            ) : reg.status !== 'cancelled' ? (
                                              <Button
                                                size="sm"
                                                variant="ghost"
                                                className="h-7 px-2 text-xs text-slate-400 hover:text-slate-600"
                                                onClick={() => handleUpdatePaymentStatus(reg.id, 'pending', date.id)}
                                                disabled={updatingPayment === reg.id}
                                              >
                                                {updatingPayment === reg.id ? '...' : (
                                                  <>
                                                    <RotateCcw className="w-3 h-3 mr-1" />
                                                    Zurücksetzen
                                                  </>
                                                )}
                                              </Button>
                                            ) : null}
                                          </div>
                                          {getBookingStatusBadge(reg)}
                                          {reg.status === 'reschedule_requested' && reg.requested_new_date && (
                                            <span className="text-xs text-slate-500">→ {reg.requested_new_date}</span>
                                          )}
                                        </div>
                                      </td>
                                      <td className="p-3 text-center">
                                        <Button
                                          size="sm"
                                          variant={reg.is_present ? "default" : "outline"}
                                          className={`h-8 px-3 text-xs ${
                                            reg.is_present 
                                              ? 'bg-emerald-600 hover:bg-emerald-700 text-white' 
                                              : 'text-slate-500 hover:text-emerald-600 hover:border-emerald-300'
                                          }`}
                                          onClick={() => handleUpdateAttendance(reg.id, !reg.is_present, date.id)}
                                          disabled={updatingAttendance === reg.id}
                                          data-testid={`attendance-btn-${reg.id}`}
                                        >
                                          {updatingAttendance === reg.id ? '...' : (
                                            <>
                                              {reg.is_present ? (
                                                <>
                                                  <UserCheck className="w-3 h-3 mr-1" />
                                                  Anwesend
                                                </>
                                              ) : (
                                                <>
                                                  <UserX className="w-3 h-3 mr-1" />
                                                  Markieren
                                                </>
                                              )}
                                            </>
                                          )}
                                        </Button>
                                      </td>
                                        <td className="p-3 text-slate-600 text-xs">
                                          {reg.created_at_formatted || reg.created_at}
                                        </td>
                                        <td className="p-3 text-center">
                                          <Button
                                            size="sm"
                                            variant="ghost"
                                            className="h-8 w-8 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                                            onClick={() => openDeleteModal(reg)}
                                            title="Anmeldung löschen"
                                          >
                                            <Trash2 className="w-4 h-4" />
                                          </Button>
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                  
                  {dashboardData.filter(d => d.total_registrations > 0 || d.spots_left < d.max_spots).length === 0 && (
                    <div className="text-center py-12 text-slate-500">
                      <Users className="w-12 h-12 mx-auto mb-4 opacity-50" />
                      <p>Noch keine Anmeldungen vorhanden</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Delete Registration Modal */}
            {deleteModalOpen && registrationToDelete && (
              <>
                <div 
                  className="fixed inset-0 bg-black/50 z-40"
                  onClick={closeDeleteModal}
                />
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                  <div className="bg-white rounded-xl shadow-2xl max-w-md w-full overflow-hidden">
                    <div className="bg-red-600 px-6 py-4 flex items-center gap-3">
                      <AlertTriangle className="w-6 h-6 text-white" />
                      <h2 className="text-white text-lg font-semibold">Anmeldung löschen</h2>
                    </div>
                    <div className="p-6">
                      <div className="mb-4 p-4 bg-slate-50 rounded-lg">
                        <p className="font-semibold text-[#1e3a5f]">
                          {registrationToDelete.first_name} {registrationToDelete.last_name}
                        </p>
                        <p className="text-sm text-slate-600">{registrationToDelete.email}</p>
                        <p className="text-xs text-slate-500 mt-1">Ref: {registrationToDelete.reference}</p>
                      </div>
                      
                      <p className="text-slate-700 text-sm mb-4">
                        <strong>Achtung:</strong> Diese Anmeldung wird dauerhaft gelöscht. 
                        Der freie Platz wird automatisch wieder freigegeben.
                        <br /><br />
                        <span className="text-red-600">Dieser Vorgang kann nicht rückgängig gemacht werden.</span>
                      </p>
                      
                      <div className="mb-4">
                        <label className="block text-sm font-medium text-slate-700 mb-2">
                          Zur Bestätigung bitte <span className="font-bold text-red-600">LÖSCHEN</span> eingeben:
                        </label>
                        <input
                          type="text"
                          value={deleteConfirmText}
                          onChange={(e) => setDeleteConfirmText(e.target.value.toUpperCase())}
                          placeholder="LÖSCHEN"
                          className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                          autoFocus
                        />
                      </div>
                      
                      <div className="flex gap-3">
                        <Button
                          variant="outline"
                          className="flex-1"
                          onClick={closeDeleteModal}
                        >
                          Abbrechen
                        </Button>
                        <Button
                          className="flex-1 bg-red-600 hover:bg-red-700 disabled:bg-red-300"
                          onClick={handleDeleteRegistration}
                          disabled={deleteConfirmText !== 'LÖSCHEN' || deletingRegistration}
                        >
                          {deletingRegistration ? (
                            'Löschen...'
                          ) : (
                            <>
                              <Trash2 className="w-4 h-4 mr-2" />
                              Endgültig löschen
                            </>
                          )}
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}
          </>
        )}

        {/* REVIEWS TAB */}
        {activeTab === 'reviews' && (
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-[#1e3a5f]">Bewertungen verwalten</CardTitle>
              <Button
                onClick={() => setShowNewReviewForm(true)}
                className="bg-red-600 hover:bg-red-700"
                size="sm"
              >
                <Plus className="w-4 h-4 mr-2" />
                Neue Bewertung
              </Button>
            </CardHeader>
            <CardContent>
              {/* New Review Form */}
              {showNewReviewForm && (
                <div className="mb-6 p-4 bg-slate-50 rounded-lg border">
                  <h4 className="font-semibold text-[#1e3a5f] mb-4">Neue Bewertung erstellen</h4>
                  <div className="space-y-4">
                    <div>
                      <Label>Bewertungstext *</Label>
                      <Textarea
                        value={newReview.text}
                        onChange={(e) => setNewReview({...newReview, text: e.target.value})}
                        placeholder="Text der Bewertung..."
                        rows={3}
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Autor *</Label>
                        <Input
                          value={newReview.author}
                          onChange={(e) => setNewReview({...newReview, author: e.target.value})}
                          placeholder="Name des Autors"
                        />
                      </div>
                      <div>
                        <Label>Bewertung (Sterne)</Label>
                        <Input
                          type="number"
                          min="1"
                          max="5"
                          value={newReview.rating}
                          onChange={(e) => setNewReview({...newReview, rating: parseInt(e.target.value)})}
                        />
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={newReview.is_active}
                        onCheckedChange={(checked) => setNewReview({...newReview, is_active: checked})}
                      />
                      <Label>Sofort aktiv</Label>
                    </div>
                    <div className="flex gap-2">
                      <Button onClick={handleCreateReview} className="bg-green-600 hover:bg-green-700">
                        <Save className="w-4 h-4 mr-2" />
                        Speichern
                      </Button>
                      <Button variant="outline" onClick={() => setShowNewReviewForm(false)}>
                        <X className="w-4 h-4 mr-2" />
                        Abbrechen
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              {/* Reviews List */}
              {loadingReviews ? (
                <div className="text-center py-8 text-slate-500">Laden...</div>
              ) : (
                <div className="space-y-3">
                  {reviews.map((review, index) => (
                    <div 
                      key={review.id} 
                      className={`p-4 rounded-lg border ${review.is_active ? 'bg-white' : 'bg-slate-100 opacity-60'}`}
                    >
                      {editingReview?.id === review.id ? (
                        // Edit Mode
                        <div className="space-y-3">
                          <Textarea
                            value={editingReview.text}
                            onChange={(e) => setEditingReview({...editingReview, text: e.target.value})}
                            rows={3}
                          />
                          <div className="grid grid-cols-2 gap-4">
                            <Input
                              value={editingReview.author}
                              onChange={(e) => setEditingReview({...editingReview, author: e.target.value})}
                              placeholder="Autor"
                            />
                            <Input
                              type="number"
                              min="1"
                              max="5"
                              value={editingReview.rating}
                              onChange={(e) => setEditingReview({...editingReview, rating: parseInt(e.target.value)})}
                            />
                          </div>
                          <div className="flex gap-2">
                            <Button size="sm" onClick={() => handleUpdateReview(review.id)} className="bg-green-600 hover:bg-green-700">
                              <Save className="w-4 h-4 mr-1" />
                              Speichern
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => setEditingReview(null)}>
                              <X className="w-4 h-4 mr-1" />
                              Abbrechen
                            </Button>
                          </div>
                        </div>
                      ) : (
                        // View Mode
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <div className="flex text-yellow-400">
                                {[...Array(review.rating)].map((_, i) => (
                                  <Star key={i} className="w-4 h-4 fill-current" />
                                ))}
                              </div>
                              <span className="text-sm font-medium text-[#1e3a5f]">{review.author}</span>
                              {!review.is_active && (
                                <span className="text-xs bg-slate-200 px-2 py-0.5 rounded">Deaktiviert</span>
                              )}
                            </div>
                            <p className="text-slate-600 text-sm">{review.text}</p>
                          </div>
                          <div className="flex items-center gap-1">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleMoveReview(review.id, 'up')}
                              disabled={index === 0}
                              className="h-8 w-8 p-0"
                            >
                              <ArrowUp className="w-4 h-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleMoveReview(review.id, 'down')}
                              disabled={index === reviews.length - 1}
                              className="h-8 w-8 p-0"
                            >
                              <ArrowDown className="w-4 h-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleToggleReviewActive(review)}
                              className="h-8 w-8 p-0"
                            >
                              {review.is_active ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => setEditingReview(review)}
                              className="h-8 w-8 p-0"
                            >
                              <Edit2 className="w-4 h-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleDeleteReview(review.id)}
                              className="h-8 w-8 p-0 text-red-600 hover:text-red-700"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                  
                  {reviews.length === 0 && (
                    <div className="text-center py-12 text-slate-500">
                      <Star className="w-12 h-12 mx-auto mb-4 opacity-50" />
                      <p>Noch keine Bewertungen vorhanden</p>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* FAQ TAB */}
        {activeTab === 'faqs' && (
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-[#1e3a5f]">FAQ verwalten</CardTitle>
              <Button
                onClick={() => setShowNewFaqForm(true)}
                className="bg-red-600 hover:bg-red-700"
                size="sm"
              >
                <Plus className="w-4 h-4 mr-2" />
                Neue FAQ
              </Button>
            </CardHeader>
            <CardContent>
              {/* New FAQ Form */}
              {showNewFaqForm && (
                <div className="mb-6 p-4 bg-slate-50 rounded-lg border">
                  <h4 className="font-semibold text-[#1e3a5f] mb-4">Neue FAQ erstellen</h4>
                  <div className="space-y-4">
                    <div>
                      <Label>Frage *</Label>
                      <Input
                        value={newFaq.question}
                        onChange={(e) => setNewFaq({...newFaq, question: e.target.value})}
                        placeholder="Die Frage..."
                      />
                    </div>
                    <div>
                      <Label>Antwort *</Label>
                      <Textarea
                        value={newFaq.answer}
                        onChange={(e) => setNewFaq({...newFaq, answer: e.target.value})}
                        placeholder="Die Antwort..."
                        rows={4}
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={newFaq.is_active}
                        onCheckedChange={(checked) => setNewFaq({...newFaq, is_active: checked})}
                      />
                      <Label>Sofort aktiv</Label>
                    </div>
                    <div className="flex gap-2">
                      <Button onClick={handleCreateFaq} className="bg-green-600 hover:bg-green-700">
                        <Save className="w-4 h-4 mr-2" />
                        Speichern
                      </Button>
                      <Button variant="outline" onClick={() => setShowNewFaqForm(false)}>
                        <X className="w-4 h-4 mr-2" />
                        Abbrechen
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              {/* FAQ List */}
              {loadingFaqs ? (
                <div className="text-center py-8 text-slate-500">Laden...</div>
              ) : (
                <div className="space-y-3">
                  {faqs.map((faq, index) => (
                    <div 
                      key={faq.id} 
                      className={`p-4 rounded-lg border ${faq.is_active ? 'bg-white' : 'bg-slate-100 opacity-60'}`}
                    >
                      {editingFaq?.id === faq.id ? (
                        // Edit Mode
                        <div className="space-y-3">
                          <Input
                            value={editingFaq.question}
                            onChange={(e) => setEditingFaq({...editingFaq, question: e.target.value})}
                            placeholder="Frage"
                          />
                          <Textarea
                            value={editingFaq.answer}
                            onChange={(e) => setEditingFaq({...editingFaq, answer: e.target.value})}
                            rows={4}
                          />
                          <div className="flex gap-2">
                            <Button size="sm" onClick={() => handleUpdateFaq(faq.id)} className="bg-green-600 hover:bg-green-700">
                              <Save className="w-4 h-4 mr-1" />
                              Speichern
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => setEditingFaq(null)}>
                              <X className="w-4 h-4 mr-1" />
                              Abbrechen
                            </Button>
                          </div>
                        </div>
                      ) : (
                        // View Mode
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <HelpCircle className="w-4 h-4 text-[#1e3a5f]" />
                              <h4 className="font-semibold text-[#1e3a5f]">{faq.question}</h4>
                              {!faq.is_active && (
                                <span className="text-xs bg-slate-200 px-2 py-0.5 rounded">Deaktiviert</span>
                              )}
                            </div>
                            <p className="text-slate-600 text-sm whitespace-pre-wrap pl-6">{faq.answer}</p>
                          </div>
                          <div className="flex items-center gap-1">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleMoveFaq(faq.id, 'up')}
                              disabled={index === 0}
                              className="h-8 w-8 p-0"
                            >
                              <ArrowUp className="w-4 h-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleMoveFaq(faq.id, 'down')}
                              disabled={index === faqs.length - 1}
                              className="h-8 w-8 p-0"
                            >
                              <ArrowDown className="w-4 h-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleToggleFaqActive(faq)}
                              className="h-8 w-8 p-0"
                            >
                              {faq.is_active ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => setEditingFaq(faq)}
                              className="h-8 w-8 p-0"
                            >
                              <Edit2 className="w-4 h-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleDeleteFaq(faq.id)}
                              className="h-8 w-8 p-0 text-red-600 hover:text-red-700"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                  
                  {faqs.length === 0 && (
                    <div className="text-center py-12 text-slate-500">
                      <HelpCircle className="w-12 h-12 mx-auto mb-4 opacity-50" />
                      <p>Noch keine FAQs vorhanden</p>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* MESSAGES TAB */}
        {activeTab === 'messages' && (
          <Card>
            <CardHeader>
              <CardTitle className="text-[#1e3a5f]">Kontaktanfragen</CardTitle>
            </CardHeader>
            <CardContent>
              {loadingMessages ? (
                <div className="text-center py-8 text-slate-500">Laden...</div>
              ) : contactMessages.length === 0 ? (
                <div className="text-center py-12 text-slate-500">
                  <Inbox className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>Keine Kontaktanfragen vorhanden</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {contactMessages.map((msg) => (
                    <div 
                      key={msg.id} 
                      className={`p-4 rounded-lg border ${msg.is_read ? 'bg-white' : 'bg-blue-50 border-blue-200'}`}
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <span className="font-semibold text-[#1e3a5f]">{msg.name}</span>
                            {!msg.is_read && (
                              <span className="text-xs bg-blue-500 text-white px-2 py-0.5 rounded">Neu</span>
                            )}
                            <span className="text-xs text-slate-500">{msg.created_at_formatted}</span>
                          </div>
                          <p className="text-sm text-slate-500 mb-1">
                            <Mail className="w-3 h-3 inline mr-1" />
                            {msg.email}
                            {msg.phone && (
                              <> · <Phone className="w-3 h-3 inline mr-1" />{msg.phone}</>
                            )}
                          </p>
                          <p className="font-medium text-slate-700 mb-2">Betreff: {msg.subject}</p>
                          <p className="text-slate-600 text-sm whitespace-pre-wrap bg-slate-50 p-3 rounded">{msg.message}</p>
                        </div>
                        <div className="flex items-center gap-1">
                          {!msg.is_read && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleMarkMessageRead(msg.id)}
                              className="h-8 px-2"
                            >
                              <CheckCircle className="w-4 h-4 mr-1" />
                              Gelesen
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleDeleteMessage(msg.id)}
                            className="h-8 w-8 p-0 text-red-600 hover:text-red-700"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* STATISTICS TAB */}
        {activeTab === 'statistics' && (
          <>
            {/* Period Selector */}
            <div className="mb-6 flex flex-wrap items-center gap-2">
              <span className="text-sm text-slate-600 mr-2">Zeitraum:</span>
              {[
                { key: 'current_month', label: 'Aktueller Monat' },
                { key: 'last_month', label: 'Letzter Monat' },
                { key: 'last_quarter', label: 'Letztes Quartal' },
                { key: 'year', label: 'Gesamtjahr' }
              ].map(period => (
                <Button
                  key={period.key}
                  variant={statisticsPeriod === period.key ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => handlePeriodChange(period.key)}
                  className={statisticsPeriod === period.key ? 'bg-[#1e3a5f] hover:bg-[#2d4a6f]' : ''}
                >
                  {period.label}
                </Button>
              ))}
            </div>

            {loadingStatistics ? (
              <div className="text-center py-12 text-slate-500">Laden...</div>
            ) : statistics ? (
              <>
                {/* Period Label */}
                <h2 className="text-xl font-semibold text-[#1e3a5f] mb-6">
                  Statistik: {statistics.period_label}
                </h2>

                {/* Key Metrics */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                  <Card>
                    <CardContent className="p-4">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                          <Users className="w-6 h-6 text-blue-600" />
                        </div>
                        <div>
                          <p className="text-sm text-slate-500">Anmeldungen</p>
                          <p className="text-3xl font-bold text-[#1e3a5f]">{statistics.summary.total_registrations}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="p-4">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center">
                          <Wallet className="w-6 h-6 text-emerald-600" />
                        </div>
                        <div>
                          <p className="text-sm text-slate-500">Umsatz (bezahlt)</p>
                          <p className="text-3xl font-bold text-emerald-600">{statistics.summary.paid_revenue.toFixed(2)} €</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="p-4">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center">
                          <Clock className="w-6 h-6 text-yellow-600" />
                        </div>
                        <div>
                          <p className="text-sm text-slate-500">Ausstehend</p>
                          <p className="text-3xl font-bold text-yellow-600">{statistics.summary.pending_revenue.toFixed(2)} €</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="p-4">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                          <TrendingUp className="w-6 h-6 text-purple-600" />
                        </div>
                        <div>
                          <p className="text-sm text-slate-500">Bezahlrate</p>
                          <p className="text-3xl font-bold text-purple-600">{statistics.summary.conversion_rate}%</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Charts Row */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
                  {/* Registrations Over Time */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-[#1e3a5f] text-lg">Anmeldungen im Zeitverlauf</CardTitle>
                    </CardHeader>
                    <CardContent>
                      {statistics.chart_data.length > 0 ? (
                        <ResponsiveContainer width="100%" height={250}>
                          <BarChart data={statistics.chart_data}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="display_date" tick={{ fontSize: 12 }} />
                            <YAxis tick={{ fontSize: 12 }} />
                            <Tooltip 
                              formatter={(value, name) => [value, name === 'registrations' ? 'Anmeldungen' : name]}
                              labelFormatter={(label) => `Datum: ${label}`}
                            />
                            <Bar dataKey="registrations" fill="#1e3a5f" name="Anmeldungen" radius={[4, 4, 0, 0]} />
                          </BarChart>
                        </ResponsiveContainer>
                      ) : (
                        <div className="h-[250px] flex items-center justify-center text-slate-500">
                          Keine Daten für diesen Zeitraum
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  {/* Payment Method Distribution */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-[#1e3a5f] text-lg">Zahlungsarten</CardTitle>
                    </CardHeader>
                    <CardContent>
                      {statistics.summary.total_registrations > 0 ? (
                        <ResponsiveContainer width="100%" height={250}>
                          <RechartsPie>
                            <Pie
                              data={[
                                { name: 'PayPal', value: statistics.registrations_by_method.paypal, color: '#0070ba' },
                                { name: 'Überweisung', value: statistics.registrations_by_method.transfer, color: '#1e3a5f' },
                                { name: 'Barzahlung', value: statistics.registrations_by_method.cash, color: '#10b981' }
                              ].filter(d => d.value > 0)}
                              cx="50%"
                              cy="50%"
                              labelLine={false}
                              label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                              outerRadius={80}
                              fill="#8884d8"
                              dataKey="value"
                            >
                              {[
                                { name: 'PayPal', value: statistics.registrations_by_method.paypal, color: '#0070ba' },
                                { name: 'Überweisung', value: statistics.registrations_by_method.transfer, color: '#1e3a5f' },
                                { name: 'Barzahlung', value: statistics.registrations_by_method.cash, color: '#10b981' }
                              ].filter(d => d.value > 0).map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={entry.color} />
                              ))}
                            </Pie>
                            <Legend />
                            <Tooltip formatter={(value) => [`${value} Anmeldungen`, '']} />
                          </RechartsPie>
                        </ResponsiveContainer>
                      ) : (
                        <div className="h-[250px] flex items-center justify-center text-slate-500">
                          Keine Daten für diesen Zeitraum
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </div>

                {/* Detailed Stats */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
                  {/* Payment Status Breakdown */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-[#1e3a5f] text-lg">Zahlungsstatus</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-4">
                        <div className="flex items-center justify-between p-3 bg-emerald-50 rounded-lg">
                          <div className="flex items-center gap-3">
                            <CheckCircle className="w-5 h-5 text-emerald-600" />
                            <span className="font-medium text-slate-700">Bezahlt</span>
                          </div>
                          <div className="text-right">
                            <p className="font-bold text-emerald-600">{statistics.summary.paid_registrations} Buchungen</p>
                            <p className="text-sm text-slate-500">{statistics.summary.paid_revenue.toFixed(2)} €</p>
                          </div>
                        </div>
                        <div className="flex items-center justify-between p-3 bg-yellow-50 rounded-lg">
                          <div className="flex items-center gap-3">
                            <Clock className="w-5 h-5 text-yellow-600" />
                            <span className="font-medium text-slate-700">Ausstehend</span>
                          </div>
                          <div className="text-right">
                            <p className="font-bold text-yellow-600">{statistics.summary.pending_registrations} Buchungen</p>
                            <p className="text-sm text-slate-500">{statistics.summary.pending_revenue.toFixed(2)} €</p>
                          </div>
                        </div>
                        <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                          <div className="flex items-center gap-3">
                            <UserCheck className="w-5 h-5 text-blue-600" />
                            <span className="font-medium text-slate-700">Anwesend markiert</span>
                          </div>
                          <div className="text-right">
                            <p className="font-bold text-blue-600">{statistics.summary.present_count} Teilnehmer</p>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Revenue by Payment Method */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-[#1e3a5f] text-lg">Umsatz nach Zahlungsart</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-4">
                        <div className="flex items-center justify-between p-3 bg-[#0070ba]/10 rounded-lg">
                          <div className="flex items-center gap-3">
                            <CreditCard className="w-5 h-5 text-[#0070ba]" />
                            <span className="font-medium text-slate-700">PayPal</span>
                          </div>
                          <div className="text-right">
                            <p className="font-bold text-[#0070ba]">{statistics.revenue_by_method.paypal.toFixed(2)} €</p>
                            <p className="text-sm text-slate-500">{statistics.registrations_by_method.paypal} Buchungen</p>
                          </div>
                        </div>
                        <div className="flex items-center justify-between p-3 bg-[#1e3a5f]/10 rounded-lg">
                          <div className="flex items-center gap-3">
                            <CreditCard className="w-5 h-5 text-[#1e3a5f]" />
                            <span className="font-medium text-slate-700">Überweisung</span>
                          </div>
                          <div className="text-right">
                            <p className="font-bold text-[#1e3a5f]">{statistics.revenue_by_method.transfer.toFixed(2)} €</p>
                            <p className="text-sm text-slate-500">{statistics.registrations_by_method.transfer} Buchungen</p>
                          </div>
                        </div>
                        <div className="flex items-center justify-between p-3 bg-emerald-50 rounded-lg">
                          <div className="flex items-center gap-3">
                            <Euro className="w-5 h-5 text-emerald-600" />
                            <span className="font-medium text-slate-700">Barzahlung</span>
                          </div>
                          <div className="text-right">
                            <p className="font-bold text-emerald-600">{statistics.revenue_by_method.cash.toFixed(2)} €</p>
                            <p className="text-sm text-slate-500">{statistics.registrations_by_method.cash} Buchungen</p>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Course Occupancy */}
                {statistics.occupancy_data.length > 0 && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-[#1e3a5f] text-lg">Kursauslastung (nächste Termine)</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead className="bg-slate-100">
                            <tr>
                              <th className="text-left p-3 font-semibold text-[#1e3a5f]">Termin</th>
                              <th className="text-center p-3 font-semibold text-[#1e3a5f]">Belegt</th>
                              <th className="text-center p-3 font-semibold text-[#1e3a5f]">Verfügbar</th>
                              <th className="text-center p-3 font-semibold text-[#1e3a5f]">Auslastung</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y">
                            {statistics.occupancy_data.map((date) => (
                              <tr key={date.date_id} className="hover:bg-slate-50">
                                <td className="p-3 font-medium text-[#1e3a5f]">{date.display_date}</td>
                                <td className="p-3 text-center">{date.occupied} / {date.max_spots}</td>
                                <td className="p-3 text-center">
                                  <span className={`font-medium ${date.spots_left > 5 ? 'text-green-600' : date.spots_left > 0 ? 'text-yellow-600' : 'text-red-600'}`}>
                                    {date.spots_left} Plätze
                                  </span>
                                </td>
                                <td className="p-3 text-center">
                                  <div className="flex items-center justify-center gap-2">
                                    <div className="w-24 h-2 bg-slate-200 rounded-full overflow-hidden">
                                      <div 
                                        className={`h-full rounded-full ${date.occupancy_rate >= 80 ? 'bg-red-500' : date.occupancy_rate >= 50 ? 'bg-yellow-500' : 'bg-green-500'}`}
                                        style={{ width: `${date.occupancy_rate}%` }}
                                      />
                                    </div>
                                    <span className="text-xs text-slate-600">{date.occupancy_rate}%</span>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </>
            ) : (
              <div className="text-center py-12 text-slate-500">
                <BarChart3 className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>Keine Statistiken verfügbar</p>
              </div>
            )}
          </>
        )}

        {/* CERTIFICATE REQUESTS TAB */}
        {activeTab === 'certificates' && (
          <>
            <div className="mb-6">
              <h2 className="text-xl font-bold text-[#1e3a5f] mb-2">Ersatzbescheinigungen</h2>
              <p className="text-slate-500">Verwalten Sie Anfragen für Ersatzbescheinigungen</p>
            </div>

            {loadingCertificates ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600 mx-auto"></div>
                <p className="mt-4 text-slate-500">Laden...</p>
              </div>
            ) : certificateRequests.length === 0 ? (
              <div className="text-center py-12 text-slate-500">
                <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>Keine Anfragen vorhanden</p>
              </div>
            ) : (
              <div className="space-y-4">
                {certificateRequests.map((request) => (
                  <Card key={request.id} className={`border-l-4 ${
                    request.status === 'neu' ? 'border-l-orange-500' :
                    request.status === 'geprueft' ? 'border-l-blue-500' :
                    'border-l-green-500'
                  }`}>
                    <CardContent className="p-4">
                      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                        {/* Request Info */}
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <h3 className="font-semibold text-[#1e3a5f]">
                              {request.first_name} {request.last_name}
                            </h3>
                            <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                              request.status === 'neu' ? 'bg-orange-100 text-orange-700' :
                              request.status === 'geprueft' ? 'bg-blue-100 text-blue-700' :
                              'bg-green-100 text-green-700'
                            }`}>
                              {request.status === 'neu' ? 'Neu' :
                               request.status === 'geprueft' ? 'Geprüft' :
                               'Bereit zur Abholung'}
                            </span>
                          </div>
                          
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm text-slate-600">
                            <div className="flex items-center gap-2">
                              <Mail className="w-4 h-4 text-slate-400" />
                              <a href={`mailto:${request.email}`} className="hover:text-red-600">
                                {request.email}
                              </a>
                            </div>
                            <div className="flex items-center gap-2">
                              <Phone className="w-4 h-4 text-slate-400" />
                              <a href={`tel:${request.phone}`} className="hover:text-red-600">
                                {request.phone}
                              </a>
                            </div>
                            <div className="flex items-center gap-2">
                              <Calendar className="w-4 h-4 text-slate-400" />
                              <span>Kursdatum: {request.course_date}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <Clock className="w-4 h-4 text-slate-400" />
                              <span>Eingegangen: {new Date(request.created_at).toLocaleDateString('de-DE')}</span>
                            </div>
                          </div>
                          
                          {request.message && (
                            <div className="mt-3 p-3 bg-slate-50 rounded-lg text-sm">
                              <span className="font-medium text-slate-700">Nachricht:</span>
                              <p className="text-slate-600 mt-1">{request.message}</p>
                            </div>
                          )}
                        </div>

                        {/* Actions */}
                        <div className="flex flex-col gap-2 min-w-[200px]">
                          {request.status === 'neu' && (
                            <Button
                              onClick={() => handleUpdateCertificateStatus(request.id, 'geprueft')}
                              disabled={updatingCertificate === request.id}
                              className="bg-blue-600 hover:bg-blue-700 text-sm"
                            >
                              {updatingCertificate === request.id ? (
                                <span className="animate-spin mr-2">⏳</span>
                              ) : (
                                <CheckCircle className="w-4 h-4 mr-2" />
                              )}
                              Als geprüft markieren
                            </Button>
                          )}
                          
                          {request.status === 'geprueft' && (
                            <Button
                              onClick={() => handleUpdateCertificateStatus(request.id, 'bereit')}
                              disabled={updatingCertificate === request.id}
                              className="bg-green-600 hover:bg-green-700 text-sm"
                            >
                              {updatingCertificate === request.id ? (
                                <span className="animate-spin mr-2">⏳</span>
                              ) : (
                                <Send className="w-4 h-4 mr-2" />
                              )}
                              Bescheinigung vorbereitet
                            </Button>
                          )}
                          
                          {request.status === 'bereit' && (
                            <div className="text-center p-2 bg-green-50 rounded-lg">
                              <CheckCircle className="w-5 h-5 text-green-600 mx-auto mb-1" />
                              <span className="text-sm text-green-700">E-Mail gesendet</span>
                            </div>
                          )}
                          
                          <Button
                            onClick={() => handleDeleteCertificateRequest(request.id)}
                            variant="outline"
                            className="text-red-600 border-red-200 hover:bg-red-50 text-sm"
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Löschen
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </>
        )}

        {/* BOOKING CHANGES TAB (Cancellations & Reschedules) */}
        {activeTab === 'bookingChanges' && (
          <>
            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-500">Gesamt</p>
                      <p className="text-2xl font-bold text-[#1e3a5f]">{bookingChangesSummary.total}</p>
                    </div>
                    <RefreshCw className="w-8 h-8 text-slate-300" />
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-500">Stornierungen</p>
                      <p className="text-2xl font-bold text-red-600">{bookingChangesSummary.cancellations}</p>
                    </div>
                    <XCircle className="w-8 h-8 text-red-200" />
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-500">Umbuchungen</p>
                      <p className="text-2xl font-bold text-amber-600">{bookingChangesSummary.reschedules}</p>
                    </div>
                    <Calendar className="w-8 h-8 text-amber-200" />
                  </div>
                </CardContent>
              </Card>
            </div>

            <Card>
              <CardHeader>
                <CardTitle className="text-[#1e3a5f]">Stornierungen & Umbuchungen (Protokoll)</CardTitle>
              </CardHeader>
              <CardContent>
                {loadingBookingChanges ? (
                  <div className="text-center py-8 text-slate-500">Laden...</div>
                ) : bookingChanges.length === 0 ? (
                  <div className="text-center py-12 text-slate-500">
                    <RefreshCw className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>Noch keine Stornierungen oder Umbuchungen vorhanden</p>
                  </div>
                ) : (
                  <>
                    {/* Mobile Card View */}
                    <div className="md:hidden space-y-3">
                      {bookingChanges.map((change) => (
                        <div key={change.id} className="border rounded-lg p-4 bg-white" data-testid={`booking-change-mobile-${change.id}`}>
                          <div className="flex items-start justify-between mb-3">
                            <div>
                              {change.change_type === 'cancellation' ? (
                                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700">
                                  <XCircle className="w-3 h-3" />
                                  Stornierung
                                </span>
                              ) : (
                                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
                                  <RefreshCw className="w-3 h-3" />
                                  Umbuchung
                                </span>
                              )}
                            </div>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-8 w-8 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                              onClick={() => handleDeleteBookingChange(change.id)}
                              data-testid={`delete-booking-change-${change.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                          <p className="font-semibold text-[#1e3a5f]">{change.participant_name}</p>
                          <p className="text-xs text-slate-500 mb-3">{change.participant_email}</p>
                          <div className="grid grid-cols-2 gap-2 text-sm">
                            <div>
                              <p className="text-xs text-slate-500">Alter Termin</p>
                              <p className="text-slate-700">{change.old_date || '-'}</p>
                            </div>
                            <div>
                              <p className="text-xs text-slate-500">Neuer Termin</p>
                              <p className={change.new_date ? 'text-green-600 font-medium' : 'text-slate-400'}>
                                {change.new_date || '-'}
                              </p>
                            </div>
                            <div>
                              <p className="text-xs text-slate-500">Zahlungsart</p>
                              <p className="text-slate-700">
                                {change.payment_method === 'cash' && 'Barzahlung'}
                                {change.payment_method === 'bg' && 'BG-Kurs'}
                                {change.payment_method === 'paypal' && 'PayPal'}
                                {!['cash', 'bg', 'paypal'].includes(change.payment_method) && (change.payment_method || '-')}
                              </p>
                            </div>
                            <div>
                              <p className="text-xs text-slate-500">Zeitpunkt</p>
                              <p className="text-slate-500 text-xs">
                                {change.created_at ? new Date(change.created_at).toLocaleString('de-DE', {
                                  day: '2-digit',
                                  month: '2-digit',
                                  year: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit'
                                }) : '-'}
                              </p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Desktop Table View */}
                    <div className="hidden md:block overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b bg-slate-50">
                            <th className="text-left p-3 font-semibold text-[#1e3a5f]">Typ</th>
                            <th className="text-left p-3 font-semibold text-[#1e3a5f]">Teilnehmer</th>
                            <th className="text-left p-3 font-semibold text-[#1e3a5f]">Alter Termin</th>
                            <th className="text-left p-3 font-semibold text-[#1e3a5f]">Neuer Termin</th>
                            <th className="text-left p-3 font-semibold text-[#1e3a5f]">Zahlungsart</th>
                            <th className="text-left p-3 font-semibold text-[#1e3a5f]">Zeitpunkt</th>
                            <th className="text-center p-3 font-semibold text-[#1e3a5f]">Aktion</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y">
                          {bookingChanges.map((change) => (
                            <tr key={change.id} className="hover:bg-slate-50" data-testid={`booking-change-row-${change.id}`}>
                              <td className="p-3">
                                {change.change_type === 'cancellation' ? (
                                  <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700">
                                    <XCircle className="w-3 h-3" />
                                    Stornierung
                                  </span>
                                ) : (
                                  <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
                                    <RefreshCw className="w-3 h-3" />
                                    Umbuchung
                                  </span>
                                )}
                              </td>
                              <td className="p-3">
                                <p className="font-medium text-[#1e3a5f]">{change.participant_name}</p>
                                <p className="text-xs text-slate-500">{change.participant_email}</p>
                              </td>
                              <td className="p-3 text-slate-600">
                                {change.old_date || '-'}
                              </td>
                              <td className="p-3">
                                {change.new_date ? (
                                  <span className="text-green-600 font-medium">{change.new_date}</span>
                                ) : (
                                  <span className="text-slate-400">-</span>
                                )}
                              </td>
                              <td className="p-3 text-slate-600">
                                {change.payment_method === 'cash' && 'Barzahlung'}
                                {change.payment_method === 'bg' && 'BG-Kurs'}
                                {change.payment_method === 'paypal' && 'PayPal'}
                                {!['cash', 'bg', 'paypal'].includes(change.payment_method) && (change.payment_method || '-')}
                              </td>
                              <td className="p-3 text-slate-500 text-xs">
                                {change.created_at ? new Date(change.created_at).toLocaleString('de-DE', {
                                  day: '2-digit',
                                  month: '2-digit',
                                  year: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit'
                                }) : '-'}
                              </td>
                              <td className="p-3 text-center">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-8 w-8 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                                  onClick={() => handleDeleteBookingChange(change.id)}
                                  data-testid={`delete-booking-change-desktop-${change.id}`}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          </>
        )}

        {/* SETTINGS TAB */}
        {activeTab === 'settings' && (
          <>
            <Card className="mb-6">
              <CardHeader>
                <CardTitle className="text-[#1e3a5f] flex items-center gap-2">
                  <Settings className="w-5 h-5" />
                  Einstellungen
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  {/* Database Reset Section */}
                  <div className="border border-red-200 rounded-lg p-6 bg-red-50">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <AlertTriangle className="w-6 h-6 text-red-600" />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-red-800 mb-2">
                          Datenbank zurücksetzen
                        </h3>
                        <p className="text-sm text-red-700 mb-4">
                          <strong>Achtung:</strong> Diese Aktion löscht alle Anmeldungen, Buchungsänderungen, 
                          Ersatzbescheinigung-Anfragen und Kontaktnachrichten. 
                          Die Kurstermine bleiben erhalten und werden auf 20 freie Plätze zurückgesetzt.
                        </p>
                        <p className="text-sm text-red-700 mb-4">
                          <strong>Diese Aktion kann nicht rückgängig gemacht werden!</strong>
                        </p>
                        
                        {!showResetConfirm ? (
                          <Button 
                            variant="destructive"
                            onClick={() => setShowResetConfirm(true)}
                            className="bg-red-600 hover:bg-red-700"
                          >
                            <TrashIcon className="w-4 h-4 mr-2" />
                            Datenbank zurücksetzen
                          </Button>
                        ) : (
                          <div className="space-y-4 p-4 bg-white rounded-lg border border-red-300">
                            <p className="text-sm font-medium text-red-800">
                              Geben Sie "RESET" ein, um die Aktion zu bestätigen:
                            </p>
                            <Input
                              type="text"
                              value={resetConfirmText}
                              onChange={(e) => setResetConfirmText(e.target.value.toUpperCase())}
                              placeholder="RESET eingeben"
                              className="max-w-xs border-red-300 focus:border-red-500"
                            />
                            <div className="flex gap-3">
                              <Button 
                                variant="destructive"
                                onClick={handleDatabaseReset}
                                disabled={resettingDatabase || resetConfirmText !== 'RESET'}
                                className="bg-red-600 hover:bg-red-700"
                              >
                                {resettingDatabase ? (
                                  <>
                                    <RotateCcw className="w-4 h-4 mr-2 animate-spin" />
                                    Wird zurückgesetzt...
                                  </>
                                ) : (
                                  <>
                                    <TrashIcon className="w-4 h-4 mr-2" />
                                    Jetzt zurücksetzen
                                  </>
                                )}
                              </Button>
                              <Button 
                                variant="outline"
                                onClick={() => {
                                  setShowResetConfirm(false);
                                  setResetConfirmText('');
                                }}
                              >
                                Abbrechen
                              </Button>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Info Section */}
                  <div className="border border-slate-200 rounded-lg p-6 bg-slate-50">
                    <h3 className="text-lg font-semibold text-[#1e3a5f] mb-3">
                      Was wird gelöscht?
                    </h3>
                    <ul className="space-y-2 text-sm text-slate-600">
                      <li className="flex items-center gap-2">
                        <XCircle className="w-4 h-4 text-red-500" />
                        Alle Anmeldungen
                      </li>
                      <li className="flex items-center gap-2">
                        <XCircle className="w-4 h-4 text-red-500" />
                        Alle Buchungsänderungen (Stornos & Umbuchungen)
                      </li>
                      <li className="flex items-center gap-2">
                        <XCircle className="w-4 h-4 text-red-500" />
                        Alle Ersatzbescheinigung-Anfragen
                      </li>
                      <li className="flex items-center gap-2">
                        <XCircle className="w-4 h-4 text-red-500" />
                        Alle Kontaktnachrichten
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-green-500" />
                        Kurstermine bleiben erhalten (20 Plätze pro Termin)
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-green-500" />
                        Bewertungen bleiben erhalten
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-green-500" />
                        FAQs bleiben erhalten
                      </li>
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>
          </>
        )}
      </main>
    </div>
  );
};

export default AdminPage;
